/**
 * useUserSettings Hook
 *
 * Feature hook for the settings category page.
 * Uses prefix-based storage listener to reactively stay in sync
 * with all user settings, filtered by category.
 */

import { useState, useCallback, useMemo } from 'react';
import { useListenToSecureStorageUpdatesByPrefix } from '@/data/storage/local/secureStorageWebCrypto';
import { toggleUserSettingUseCase } from '@/domain/usecases/settings/toggleUserSettingUseCase';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import type { UserSettingStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { UserSettingCategory } from '@/domain/enums/settings/userSettingCategory';

export function useUserSettings(category: UserSettingCategory) {
  const [isToggling, setIsToggling] = useState<Record<string, boolean>>({});

  const { items: allSettings, loading: isLoading } =
    useListenToSecureStorageUpdatesByPrefix<UserSettingStorage>(
      SECURE_STORAGE_KEYS.USER_SETTINGS,
      (setting) => setting.name,
      true,
    );

  const settings = useMemo(
    () => allSettings.filter((s) => s.category === category),
    [allSettings, category]
  );

  const toggleSetting = useCallback(async (settingName: string) => {
    setIsToggling((prev) => ({ ...prev, [settingName]: true }));

    try {
      await toggleUserSettingUseCase.execute(settingName);
    } finally {
      setIsToggling((prev) => ({ ...prev, [settingName]: false }));
    }
  }, []);

  return {
    settings,
    isLoading,
    isToggling,
    toggleSetting,
  };
}
